import styled from 'styled-components';
import { Slider } from 'antd';
import { SIZE_SPAN, USUAL } from './useCountModel';

/**
 * The count panel's size filter (Find's size filter for a video, FindResults.SizeRow): every track's size as bars on
 * a log scale, and the range kept. In metres on the ground when the video has flight data, else in pixels. It starts
 * with every size (the stored count, the report's); the usual size of what is counted ÷ and × SIZE_SPAN is one click.
 */

const tk = ({ theme: t }) => t.token;

const BINS = 28;

const Box = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXXS}px;
`;

const Line = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: ${p => tk(p).marginXS}px;
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextSecondary};
	b {
		color: ${p => tk(p).colorTextLightSolid};
		font-weight: ${p => tk(p).fontWeightStrong};
	}
`;

const Bars = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${p => tk(p).lineWidth}px;
	height: ${p => tk(p).sizeLG}px;
	margin: 0 ${p => tk(p).sizeXS}px;
`;

const Bar = styled.div`
	flex: 1;
	min-height: ${p => tk(p).lineWidth}px;
	height: ${p => p.$height * 100}%;
	border-radius: ${p => tk(p).lineWidth}px;
	background: ${p => (p.$in ? p.$color || tk(p).colorPrimary : tk(p).colorFillSecondary)};
`;

const Dot = styled.i`
	display: inline-block;
	width: ${p => tk(p).sizeXS}px;
	height: ${p => tk(p).sizeXS}px;
	margin-right: ${p => tk(p).marginXXS}px;
	border-radius: 50%;
`;

const Small = styled.div`
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextTertiary};
	line-height: ${p => tk(p).lineHeightSM};
`;

const Link = styled.button`
	border: 0;
	padding: 0;
	background: transparent;
	color: ${p => tk(p).colorBrandLight};
	font-size: ${p => tk(p).fontSizeXS}px;
	text-decoration: underline;
	cursor: pointer;
`;

const fmt = (v, unit) =>
	unit === 'm'
		? `${v < 1 ? v.toFixed(2) : v < 10 ? v.toFixed(1) : Math.round(v)} m`
		: `${Math.round(v)} px`;

// one kind's row: its tracks' sizes as bars, the range kept, the slider
const SizeRow = ({
	unit,
	values,
	range,
	chosen,
	usual,
	usualRange,
	hidden,
	name,
	color,
	several,
	onChange,
	onReset,
}) => {
	if (!values?.length) return null;
	const lo =
		Math.max(unit === 'm' ? 0.05 : 1, Math.min(...values, range ? range[0] : Infinity)) / 1.25;
	const hi = Math.max(...values, range ? range[1] : 0) * 1.25;
	const span = Math.log(hi / lo) || 1;
	const toPosition = v => Math.round((Math.log(Math.max(v, lo) / lo) / span) * 1000);
	const toValue = position => lo * Math.exp((position / 1000) * span);
	const [min, max] = range ?? [lo, hi];
	const bins = new Array(BINS).fill(0);
	values.forEach(v => {
		const k = Math.floor((toPosition(v) / 1000) * BINS);
		bins[Math.min(BINS - 1, Math.max(0, k))] += 1;
	});
	const tallest = Math.max(...bins, 1);
	// a range from 0 (the automatic one when the camera was tilted throughout) has only an upper limit
	const keepText = !range
		? 'every size'
		: range[0] > 0
			? `${fmt(min, unit)} – ${fmt(max, unit)}`
			: `up to ${fmt(max, unit)}`;
	const inRange = k => {
		const v = toValue(((k + 0.5) / BINS) * 1000);
		return v >= min && v <= max;
	};
	return (
		<Box>
			<Line>
				<span>
					{several && <Dot style={{ background: color }} />}
					{several ? `${name} · keep ` : 'Keep '}
					<b>{keepText}</b>
					{hidden ? ` · ${hidden} hidden` : ''}
					{several && unit === 'm' && usual?.length
						? ` · usual ${fmt(usual[0], 'm')}`
						: ''}
				</span>
				{chosen ? (
					<Link type="button" onClick={onReset}>
						Keep every size
					</Link>
				) : (
					usualRange && (
						<Link type="button" onClick={() => onChange(USUAL)}>
							Keep the usual size
						</Link>
					)
				)}
			</Line>
			<Bars aria-hidden>
				{bins.map((n, k) => (
					<Bar key={k} $height={n / tallest} $in={inRange(k)} $color={color} />
				))}
			</Bars>
			<Slider
				range
				min={0}
				max={1000}
				value={[toPosition(Math.max(min, lo)), toPosition(Math.min(max, hi))]}
				tooltip={{ formatter: position => fmt(toValue(position), unit) }}
				onChange={([from, to]) =>
					onChange(from <= 0 && to >= 1000 ? null : [toValue(from), toValue(to)])
				}
				aria-label={`Sizes of ${name} to keep`}
			/>
		</Box>
	);
};

// groups: one per kind counted ([{ kind, values, range, chosen, usual, usualRange, hidden }], kind '' for a one-kind
// video)
// across: some tracks were seen with the camera tilted, measured across the image (only the upper limit holds for them)
const SizeFilter = ({
	unit,
	across = false,
	groups,
	subject,
	colors = null,
	onChange,
	onReset,
}) => {
	const rows = (groups || []).filter(g => g.values.length);
	if (!rows.length) return null;
	const several = rows.length > 1;
	// a kind's own name: a one-row filter of a several-kind video (the others had nothing measured) is that kind's
	const nameOf = g => g.kind || subject;
	const usual = rows.flatMap(g => g.usual || []);
	const startText = several
		? `Every size is counted, as in the report; each kind can keep its usual size ÷ and × ${SIZE_SPAN}.`
		: `Every size is counted, as in the report. The usual size of ${nameOf(rows[0])} is ${usual.map(u => fmt(u, 'm')).join(', ')}: keep it ÷ and × ${SIZE_SPAN} to leave out what is far off.`;
	const tiltedText = across
		? ' Seen with the camera tilted, a size is the width across the image, so only the upper limit applies to it.'
		: '';
	const pxText =
		'In pixels: this video has no flight data to give metres, and something far away looks small.';
	// once a range is kept the page counts less than the report: said, naming the kinds kept, not "every size"
	const kept = rows.filter(g => g.chosen);
	let filterText = '';
	if (kept.length) {
		const one = kept.length === 1;
		let what = `the kept range${one ? '' : 's'} of ${kept.map(nameOf).join(' and ')}`;
		if (!several)
			what = kept[0].auto
				? `the usual size of ${nameOf(kept[0])} ÷ and × ${SIZE_SPAN}`
				: 'the range you set';
		// only the usual size drops its lower limit for a tilted track; a range set by hand applies as set
		const note = unit === 'm' ? (kept.every(g => g.auto) ? tiltedText : '') : ` ${pxText}`;
		filterText = `Only ${what} ${one ? 'is' : 'are'} counted here; the report counts every size.${note}`;
	}
	let usualText = pxText;
	if (filterText) usualText = filterText;
	else if (unit === 'm' && usual.length) usualText = `${startText}${tiltedText}`;
	else if (unit === 'm') usualText = 'Every size is counted; no usual size is known for this.';
	return (
		<Box>
			{rows.map(g => (
				<SizeRow
					key={g.kind || 'all'}
					unit={unit}
					{...g}
					name={g.kind || subject}
					color={colors?.[g.kind]?.hex}
					several={several}
					onChange={r => onChange(g.kind, r)}
					onReset={() => onReset(g.kind)}
				/>
			))}
			<Small>{usualText}</Small>
		</Box>
	);
};

export default SizeFilter;
